/**
 * NEON COACH - نقطة انطلاق التطبيق الرئيسية
 */

import './styles/variables.css';
import './styles/reset.css';
import './styles/base.css';
import './styles/components.css';
import './styles/progressWeightTracker.css';
import './styles/profileHub.css';

import { Router } from './router/router.js';
import { setupTrainingLoadingInterceptors } from './utils/splash.js';
import { initNeonParticles } from './utils/particles.js';
import { initImageProtectionGuard } from './utils/imageSecurity.js';
import { initArabicNumeralsAutoConvert } from './utils/arabicNumerals.js';
import { inject } from '@vercel/analytics';
import { injectSpeedInsights } from '@vercel/speed-insights';

inject({ mode: import.meta.env.PROD ? 'production' : 'development' });
injectSpeedInsights({ debug: import.meta.env.DEV });

// تشغيل خلفية الجسيمات والنقاط الخضراء النيونية بعد تجهيز الشاشة الأولى
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => initNeonParticles(), { timeout: 1000 });
  } else {
    setTimeout(initNeonParticles, 60);
  }
}

// تفعيل رصد أزرار التدريب لعرض شاشة التحميل فوراً وحماية الوسائط
setupTrainingLoadingInterceptors();
initImageProtectionGuard();
initArabicNumeralsAutoConvert();

// تهيئة وتشغيل موجه الصفحات
function initApp() {
  const appElement = document.getElementById('app');
  if (appElement) {
    try {
      const router = new Router(appElement);
      router.init();

      // إخفاء شاشة التحميل الأولية الذكية فوراً وبسلاسة فور جاهزية الواجهة الأولى
      if (typeof window.dismissNeonSplash === 'function') {
        window.dismissNeonSplash();
      }
    } catch (err) {
      console.error('خطأ أثناء تهيئة التطبيق:', err);
      if (typeof window.dismissNeonSplash === 'function') {
        window.dismissNeonSplash();
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

// إدارة Service Worker لتجنب تعليق الكاش والشاشات البيضاء في بيئة التطوير المحلي
if ('serviceWorker' in navigator) {
  const isDevOrLocal = import.meta.env.DEV || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' || 
                       window.location.hostname.startsWith('192.168.');
  if (isDevOrLocal) {
    // إلغاء تسجيل أي Service Worker قديم لتفادي كاش الملفات أثناء التطوير المحلي
    navigator.serviceWorker.getRegistrations().then(registrations => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
    if ('caches' in window) {
      caches.keys().then(names => {
        for (const name of names) caches.delete(name);
      });
    }
  } else {
    const hadController = Boolean(navigator.serviceWorker.controller);
    let isRefreshing = false;

    if (hadController) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (isRefreshing) return;
        isRefreshing = true;
        window.location.reload();
      });
    }

    navigator.serviceWorker.register('./sw.js', { updateViaCache: 'none' })
      .then(async reg => {
        console.log('NEON COACH PWA ServiceWorker مسجل بنجاح:', reg.scope);
        const activateWorker = (worker) => {
          worker?.postMessage({ type: 'SKIP_WAITING' });
        };

        if (reg.waiting) {
          activateWorker(reg.waiting);
        }

        reg.addEventListener('updatefound', () => {
          const installingWorker = reg.installing;
          if (!installingWorker) return;

          installingWorker.addEventListener('statechange', () => {
            if (
              installingWorker.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              activateWorker(installingWorker);
            }
          });
        });

        try {
          await reg.update();
        } catch (err) {
          console.warn('ServiceWorker update check failed:', err);
        }
      })
      .catch(err => {
        console.warn('تعذر تسجيل ServiceWorker:', err);
      });
  }
}

// التقاط حدث تثبيت PWA على الجهاز
let deferredPrompt;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.log('التطبيق جاهز للتثبيت كـ PWA.');
});
